import client, { rawResponseClient } from 'shared/ApiClient';
import { downloadFileFromResponse } from 'shared';
import {
    Offer,
    OfferFormPayload,
    OfferOptions,
    OfferStats,
    OfferSummary,
    OffersQuery,
    SigningDetailsPayload,
} from 'recruit/models';
import { PagingResult } from 'models/Paging';

export async function getOffers(query: OffersQuery = {}): Promise<PagingResult<OfferSummary>> {
    const params = new URLSearchParams();
    if (query.candidateId) params.set('candidate', String(query.candidateId));
    if (query.search) params.set('search', query.search);
    if (query.status) params.set('status', query.status);
    if (query.position) params.set('position', query.position);
    if (query.location) params.set('location', query.location);
    if (query.page && query.page > 1) params.set('page', String(query.page));
    const suffix = params.toString();
    return client(`recruit/offers/${suffix ? `?${suffix}` : ''}`);
}

export async function getOfferStats(): Promise<OfferStats> {
    return client('recruit/offers/stats/');
}

export async function getActionRequiredOffers(): Promise<OfferSummary[]> {
    return client('recruit/offers/action-required/');
}

export async function getDashboardOffers(): Promise<OfferSummary[]> {
    return client('recruit/offers/dashboard/');
}

export async function getOffer(offerId: number | string): Promise<Offer> {
    return client(`recruit/offers/${offerId}/`);
}

export async function createOffer(payload: OfferFormPayload): Promise<Offer> {
    return client('recruit/offers/', { body: payload });
}

export async function updateOffer(offerId: number | string, payload: OfferFormPayload): Promise<Offer> {
    return client(`recruit/offers/${offerId}/`, { body: payload, method: 'PATCH' });
}

export async function submitOffer(offerId: number | string): Promise<Offer> {
    return client(`recruit/offers/${offerId}/submit/`, { body: {} });
}

export async function addOfferSigningDetails(offerId: number | string, payload: SigningDetailsPayload): Promise<Offer> {
    return client(`recruit/offers/${offerId}/signing-details/`, { body: payload });
}

export async function getOfferOptions(candidateId?: number | string): Promise<OfferOptions> {
    const query = candidateId ? `?candidate=${candidateId}` : '';
    return client(`recruit/offers/options/${query}`);
}

export async function approveOffer(offerId: number | string, note = ''): Promise<Offer> {
    return client(`recruit/offers/${offerId}/approve/`, { body: { note } });
}

export async function rejectOffer(offerId: number | string, note: string): Promise<Offer> {
    return client(`recruit/offers/${offerId}/reject/`, { body: { note } });
}

export async function fillOfferField(offerId: number | string, fieldId: number, value = ''): Promise<Offer> {
    return client(`recruit/offers/${offerId}/fields/${fieldId}/`, { body: { value } });
}

export async function finishOfferSigning(offerId: number | string): Promise<Offer> {
    return client(`recruit/offers/${offerId}/finish-signing/`, { body: {} });
}

export async function cancelOffer(offerId: number | string, note = ''): Promise<Offer> {
    return client(`recruit/offers/${offerId}/cancel/`, { body: { note } });
}

export async function downloadOfferDocuments(offerId: number | string): Promise<void> {
    const result = await rawResponseClient(`recruit/offers/${offerId}/documents-zip/`, { method: 'GET' });
    if (!result.ok) throw new Error('Could not download the documents.');
    await downloadFileFromResponse(result.response);
}

export async function downloadOfferDocument(offerId: number | string, documentId: number): Promise<void> {
    const result = await rawResponseClient(`recruit/offers/${offerId}/documents/${documentId}/pdf/`, { method: 'GET' });
    if (!result.ok) throw new Error('Could not download the document.');
    await downloadFileFromResponse(result.response);
}

export async function downloadSigningCertificate(offerId: number | string): Promise<void> {
    const result = await rawResponseClient(`recruit/offers/${offerId}/signing-certificate/`, { method: 'GET' });
    if (!result.ok) throw new Error('Could not download the signing certificate.');
    await downloadFileFromResponse(result.response);
}
